import Choices from "choices.js"
import flatpickr from "flatpickr"
import Toastify from "toastify-js"
import "toastify-js/src/toastify.css"
import { DateTime } from "luxon"
import * as util from "./util"
import * as utillogin from "./utillogin"

/* To locally debug this script via Webflow the best option I've found is to
add two scripts in Webflow:
<script src="http://localhost:3000/????.bundle.js"></script>
<script src="angry-boyd-1669aa.netlify.app/????.bundle.js" crossorigin="anonymous"></script>
The below check ensures that if localhost loads successfully the second
(production) script won't run.
Initially I tried to add the script dynamically in Webflow but that didn't
work - I needed it to run immediately but it wouldn't. */
if (typeof SharesyWebflow === "undefined") {
  util.initSentry()

  window.amplitudeOnLoad = function (instance) {
    util.amplitudeInit(instance, "Dashboard Opening Hours Viewed")
  }

  // Wrap everything with Revealing Module Pattern
  window.SharesyWebflow = (function () {
    var searchParams = util.getSearchParams()
    var spaceData
    var startTimes
    var endTimes
    var spaceChoices
    var savedChoices = {}

    var flatpickrCalendar
    var overrideData
    var overrideDates = [] // used to highlight dates in calendar with overrides

    const weekdays = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ]

    const ordinals = [
      "zeroth",
      "first",
      "second",
      "third",
      "fourth",
      "fifth",
      "sixth",
      "seventh",
      "eighth",
      "ninth",
      "tenth",
      "eleventh",
      "twelfth",
      "thirteenth",
      "fourteenth",
      "fifteenth",
      "sixteenth",
      "seventeenth",
      "eighteenth",
      "nineteenth",
    ]

    async function initClerk() {
      let token = await utillogin.getClerkToken()
      utillogin.displayAdminNavItems()
      await initSpaces(token)
    }

    async function initSpaces(token) {
      try {
        spaceData = await util.api.get(`get-spaces-token?token=${token}`).json()
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent(
          "Unknown Error Retrieving Spaces (Opening Hours)"
        )
        Toastify({
          text: "There was a problem getting the list of spaces. Click here to refresh and try again. If it fails again please contact venuesupport@sharesy.com.",
          duration: 10000,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "#d31818",
          },
          onClick: function () {
            window.location.reload()
          },
        }).showToast()
      }
      const spaceSelect = document.getElementById("select-space")
      spaceChoices = new Choices(spaceSelect, {
        itemSelectText: "",
        allowHTML: false,
        searchResultLimit: 10,
        choices: spaceData.map(function (d) {
          return {
            value: d.id,
            label: d.name,
          }
        }),
        shouldSort: false, // don't sort, use the order from the API
      })
      spaceSelect.addEventListener(
        "change",
        function (event) {
          updateHours(event.target.value)
          util.updateUrlParam("space", event.target.value, false, false)
        },
        false
      )

      if (searchParams.has("space")) {
        let spaceId = parseInt(searchParams.get("space"))
        spaceChoices.setChoiceByValue(spaceId)
        updateHours(spaceId)
      } else {
        spaceSelect.click()
      }
    }

    async function updateHours(spaceId) {
      let weekdaysSection = document.getElementById("weekdays")
      weekdaysSection.classList.add("hide")
      if (!spaceId) {
        return
      }
      try {
        let token = await utillogin.getClerkToken()
        let hoursData = await util.api
          .get(`get-standard-hours-token?token=${token}&space_id=${spaceId}`)
          .json()
        for (const day of weekdays) {
          let hours = hoursData.filter(
            (row) => row.weekday.toLowerCase() == day
          )
          initialiseHourSplits(day, hours)
        }
        weekdaysSection.classList.remove("hide")

        updateOverrideHours(spaceId)
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Unknown Error Retrieving Opening Hours")
        Toastify({
          text: "There was a problem getting the opening hours for the selected space. Click here to refresh and try again. If it fails again please contact venuesupport@sharesy.com.",
          duration: 10000,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "#d31818",
          },
          onClick: function () {
            window.location.reload()
          },
        }).showToast()
      }
    }

    async function updateOverrideHours(spaceId) {
      document.getElementById("day-override").classList.add("hide")
      try {
        let token = await utillogin.getClerkToken()
        overrideData = await util.api
          .get(`get-override-hours-token?token=${token}&space_id=${spaceId}`)
          .json()
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Unknown Error Retrieving Override Hours")
        Toastify({
          text: "There was a problem getting the override hours for the selected space. Click here to refresh and try again. If it fails again please contact venuesupport@sharesy.com.",
          duration: 10000,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "#d31818",
          },
          onClick: function () {
            window.location.reload()
          },
        }).showToast()
      }

      updateOverrideDates(spaceId)

      if (!flatpickrCalendar) {
        flatpickrCalendar = flatpickr("#inline-flatpickr-calendar", {
          appendTo: document.getElementById("inline-flatpickr-calendar"),
          inline: true,
          enableTime: false,
          wrap: true,
          disableMobile: "true",
          monthSelectorType: "static",
          locale: {
            firstDayOfWeek: 1, // start week on Monday
            weekdays: {
              shorthand: ["S", "M", "T", "W", "T", "F", "S"],
              longhand: [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ],
            },
          },
          onChange: function (selectedDates, dateStr, instance) {
            if (selectedDates.length > 0) {
              util.logAmplitudeEvent("Select Override Calendar Date")
              let hours = overrideData.filter(
                (row) =>
                  row.start_date ==
                  DateTime.fromJSDate(selectedDates[0], {
                    zone: util.TIMEZONE,
                  }).toISODate()
              )
              initialiseHourSplits("override", hours)
              document.getElementById(
                "override-message"
              ).innerText = `Override the standard hours for ${DateTime.fromISO(
                selectedDates[0].toISOString()
              ).toFormat("ccc dd LLL")}.`
              if (hours.length > 0) {
                document.getElementById("delete-override").value =
                  "Remove override"
                document
                  .getElementById("delete-override")
                  .classList.remove("hide")
              } else {
                document.getElementById("delete-override").classList.add("hide")
              }
              document.getElementById("day-override").classList.remove("hide")
            }
          },
          onDayCreate: function (dObj, dStr, fp, dayElem) {
            if (
              overrideDates.indexOf(
                DateTime.fromJSDate(dayElem.dateObj, {
                  zone: util.TIMEZONE,
                }).toISODate()
              ) !== -1
            ) {
              dayElem.classList.remove("muted")
              dayElem.classList.add("bold")
            } else {
              dayElem.classList.add("muted")
              dayElem.classList.remove("bold")
            }
          },
        })
      } else {
        // Do the minimal thing to trigger onDayCreate so the bolding in the
        // calendar is updated.
        flatpickrCalendar.changeMonth(0)
      }
    }

    function updateOverrideDates(spaceId) {
      overrideDates = overrideData
        .filter(function (d) {
          return d.space_id == spaceId
        })
        .map(function (d) {
          return d.start_date
        })
        .filter(function (d, i, self) {
          return self.indexOf(d) === i
        })
    }

    function prepareChoicesTimeDropdown(day, number, isStart) {
      let element = document.getElementById(
        `${isStart ? "start" : "end"}-time-${number}-${day}`
      )
      let times = endTimes
      if (isStart) {
        times = startTimes
      }
      let choices = new Choices(element, {
        itemSelectText: "",
        shouldSort: false,
        allowHTML: false,
        fuseOptions: {
          threshold: 0.0,
          ignoreLocation: true,
        },
        choices: times.map(function (d) {
          return {
            value: d,
            label: d,
          }
        }),
      })
      element.addEventListener("change", function (event) {
        hideError(element.id.split("-").pop())
      })
      savedChoices[`${day}_${number}_${isStart ? "start" : "end"}`] = choices
      return choices
    }

    function setIsOpen(day, isOpen) {
      if (day === "override") {
        return
      }
      if (isOpen) {
        document.getElementById(`time-ranges-${day}`).classList.remove("hide")
        document.getElementById(day).classList.remove("greyed")
        document.getElementById(`open-text-${day}`).classList.remove("greyed")
        document.getElementById(`open-text-${day}`).innerText = "Open"
      } else {
        document.getElementById(`time-ranges-${day}`).classList.add("hide")
        document.getElementById(day).classList.add("greyed")
        document.getElementById(`open-text-${day}`).classList.add("greyed")
        document.getElementById(`open-text-${day}`).innerText = "Closed"
      }
      hideError(day)
      if (document.getElementById(`${day}-switch`).checked != isOpen) {
        document.getElementById(`${day}-switch`).checked = isOpen
      }
    }

    /**
     * Clear and reload the hour split dropdowns.
     * @param {string} day E.g. "monday" (or "override" for the override section).
     * @param {Array} hours
     */
    function initialiseHourSplits(day, hours) {
      // Delete all splits
      let splits = document.querySelectorAll(
        `[id^="time-range-"][id$="-${day}"]`
      )
      for (let i = 1; i <= splits.length; i++) {
        removeSplit(day)
      }
      if (hours.length < 1) {
        setIsOpen(day, false)
        return
      }
      hours.forEach(function (h, i) {
        if (i > 0) {
          addSplit(day)
        }
        savedChoices[`${day}_${i + 1}_start`].setChoiceByValue(
          h.start_time_local
        )
        savedChoices[`${day}_${i + 1}_end`].setChoiceByValue(h.end_time_local)
      })
      setIsOpen(day, true)
    }

    function addSplit(day) {
      // Check how many elements there are with the id time-range-x-day
      let splits = document.querySelectorAll(
        `[id^="time-range-"][id$="-${day}"]`
      )
      let newLength = splits.length + 1
      let split = document.getElementById(`time-range-template`).cloneNode(true)
      split.id = `time-range-${newLength}-${day}`
      split.classList.remove("hide")
      split.getElementsByTagName(
        "select"
      )[0].id = `start-time-${newLength}-${day}`
      split.getElementsByTagName(
        "select"
      )[1].id = `end-time-${newLength}-${day}`
      // Append the new split before the add-split-{day} link
      document
        .getElementById(`time-ranges-${day}`)
        .insertBefore(split, document.getElementById(`add-split-${day}`))
      let newStartChoices = prepareChoicesTimeDropdown(day, newLength, true)
      prepareChoicesTimeDropdown(day, newLength, false)
      // Move the remove-split-monday button from the last split to the new split
      let removeButton = document.getElementById(`remove-split-${day}`)
      removeButton.parentNode.removeChild(removeButton)
      split.appendChild(removeButton)
      removeButton.classList.remove("hide")
      // If the end time of the previous split is set, make the start time of the new split end time + 1 hour
      let previousEndTime = document.getElementById(
        `end-time-${splits.length}-${day}`
      ).value
      if (previousEndTime) {
        let previousEndTimeHour = parseInt(previousEndTime.split(":")[0])
        let previousEndTimeMinute = parseInt(previousEndTime.split(":")[1])
        let newHour = previousEndTimeHour + 1
        if (newHour === 24) {
          newHour = 0
        }
        let newStartTime = `${("0" + newHour).slice(-2)}:${(
          "0" + previousEndTimeMinute
        ).slice(-2)}`
        newStartChoices.setChoiceByValue(newStartTime)
      }
    }

    function removeSplit(day) {
      // Check how many elements there are with the id time-range-x-day
      let splits = document.querySelectorAll(
        `[id^="time-range-"][id$="-${day}"]`
      )
      if (splits.length > 1) {
        // move the remove-split-monday button from the last split to the previous split
        let removeButton = document.getElementById(`remove-split-${day}`)
        removeButton.parentNode.removeChild(removeButton)
        let secondLastSplit = document.getElementById(
          `time-range-${splits.length - 1}-${day}`
        )
        secondLastSplit.appendChild(removeButton)
        let split = document.getElementById(
          `time-range-${splits.length}-${day}`
        )
        split.parentNode.removeChild(split)
        if (splits.length === 2) {
          removeButton.classList.add("hide")
        }
      } else {
        // If there is only one split, remove the start and end times
        savedChoices[`${day}_1_start`].setChoiceByValue("")
        savedChoices[`${day}_1_end`].setChoiceByValue("")
      }
      hideError(day)
    }

    function hideError(day) {
      let error = document.getElementById(`error-${day}`)
      error.style.display = "none"
    }

    /**
     * Return an array of opening hours (or override hours) to save.
     * @param {Array} weekdayList An array of weekdays, or ["override"] to return override hours.
     * @returns {Array}
     */
    function openingHoursToSave(weekdayList) {
      let openingHours = []
      let hasError = false
      weekdayList.forEach(function (day) {
        let weekdayHours = { day: day }
        if (day === "override") {
          weekdayHours.isOpen = true
        } else {
          weekdayHours.isOpen = document.getElementById(`${day}-switch`).checked
        }
        if (weekdayHours.isOpen) {
          let splits = document.querySelectorAll(
            `[id^="time-range-"][id$="-${day}"]`
          )
          weekdayHours.splits = []
          for (let i = 1; i <= splits.length; i++) {
            const startTimeSelect = document.getElementById(
              `start-time-${i}-${day}`
            )
            const endTimeSelect = document.getElementById(
              `end-time-${i}-${day}`
            )
            // Get the child option element with the 'selected' attribute set
            const selectedStartTimeOption = Array.from(
              startTimeSelect.options
            ).find((option) => option.selected)
            const selectedEndTimeOption = Array.from(
              endTimeSelect.options
            ).find((option) => option.selected)
            weekdayHours.splits.push({
              ordinal: ordinals[i],
              startTime: selectedStartTimeOption.value,
              endTime: selectedEndTimeOption.value,
            })
          }
          let error = checkForError(weekdayHours.splits)
          if (error) {
            let errorElem = document.getElementById(`error-${day}`)
            errorElem.innerText = error
            errorElem.style.display = "block"
            hasError = true
            if (day !== "override") {
              // Scroll to the first text for the day with the error, and go up 20px
              document.getElementById(day).scrollIntoView(true)
              window.scrollBy(0, -20)
            }
            return
          }
        }
        openingHours.push(weekdayHours)
      })
      if (hasError) {
        return null
      }
      return openingHours
    }

    function checkForError(hourSplits) {
      for (const split of hourSplits) {
        if (!split.startTime || !split.endTime) {
          return `Both the start and end time need to be selected for the ${split.ordinal} set of hours.`
        }
      }
      hourSplits.forEach((h) => {
        if (h.endTime.substring(0, 2) <= "06") {
          // E.g. convert "03:30" to "27:30" to make comparisons easy.
          // Don't change endTime as hourSplits is used outside this function.
          h.endTimeExtended =
            (parseInt(h.endTime.substring(0, 2)) + 24).toString() +
            h.endTime.substring(2)
        } else {
          h.endTimeExtended = h.endTime
        }
      })
      for (const split of hourSplits) {
        if (split.endTimeExtended <= split.startTime) {
          return `The ${split.ordinal} set of hours is not correct - the end time needs to be after the start time.`
        }
        let splitsExclCurrent = hourSplits.filter(
          (h) => h.ordinal != split.ordinal
        )
        for (const h of splitsExclCurrent) {
          if (
            (h.endTimeExtended <= split.endTimeExtended &&
              h.endTimeExtended > split.startTime) ||
            (h.startTime >= split.startTime &&
              h.startTime < split.endTimeExtended) ||
            (h.endTimeExtended > split.endTimeExtended &&
              h.startTime < split.startTime)
          ) {
            return `The ${split.ordinal} and ${h.ordinal} set of hours overlap.`
          }
        }
      }
      return ""
    }

    /**
     * Save override hours.
     * @param {Array} overrideHours The hours to save. If empty, existing override hours will be deleted.
     * @param {boolean} isDelete
     */
    async function saveOverrideHours(overrideHours, isDelete) {
      try {
        let token = await utillogin.getClerkToken()
        let spaceId = parseInt(document.getElementById("select-space").value)
        let savedOverrideHours = await util.api
          .post("save-override-hours", {
            json: {
              token: token,
              space_id: spaceId,
              start_date: DateTime.fromJSDate(
                flatpickrCalendar.selectedDates[0],
                {
                  zone: util.TIMEZONE,
                }
              ).toISODate(),
              override_hours: overrideHours,
            },
          })
          .json()
        if (savedOverrideHours && savedOverrideHours.success) {
          util.logAmplitudeEvent(
            `Override Hours ${isDelete ? "Deleted" : "Saved"}`,
            {
              Date: DateTime.fromJSDate(flatpickrCalendar.selectedDates[0], {
                zone: util.TIMEZONE,
              }).toISODate(),
            }
          )
          Toastify({
            text: `Override hours ${
              isDelete ? "removed" : "saved"
            } for ${DateTime.fromISO(
              flatpickrCalendar.selectedDates[0].toISOString()
            ).toFormat("ccc dd LLL")}.`,
            duration: 4000,
            close: true,
            gravity: "top",
            position: "center",
            stopOnFocus: true,
            style: {
              background: "#1aa979",
            },
          }).showToast()
          updateOverrideHours(spaceId)
        } else {
          util.logAmplitudeEvent(
            `Override Hours ${isDelete ? "Delete" : "Save"} Failed`,
            {
              Date: DateTime.fromJSDate(flatpickrCalendar.selectedDates[0], {
                zone: util.TIMEZONE,
              }).toISODate(),
            }
          )
          document.getElementById("save-fail-modal").style.display = "flex"
          document.getElementById("modal-container").style.display = "flex"
        }
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent(
          `Unknown Error ${isDelete ? "Deleting" : "Saving"} Override Hours`
        )
        document.getElementById("save-fail-modal").style.display = "flex"
        document.getElementById("modal-container").style.display = "flex"
      }
    }

    function init() {
      initClerk()

      startTimes = util.generateTimes(6, 23, false)
      endTimes = util.generateTimes(7, 6, true)
      for (const day of weekdays) {
        prepareChoicesTimeDropdown(day, 1, true)
        prepareChoicesTimeDropdown(day, 1, false)
      }
      prepareChoicesTimeDropdown("override", 1, true)
      prepareChoicesTimeDropdown("override", 1, false)

      try {
        weekdays.forEach(function (day) {
          document
            .getElementById(`${day}-switch`)
            .addEventListener("click", function () {
              setIsOpen(day, this.checked)
            })
          document
            .getElementById(`add-split-${day}`)
            .addEventListener("click", function () {
              addSplit(day)
            })
          document
            .getElementById(`remove-split-${day}`)
            .addEventListener("click", function () {
              removeSplit(day)
            })
        })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("add-split-override")
          .addEventListener("click", function () {
            addSplit("override")
          })
        document
          .getElementById("remove-split-override")
          .addEventListener("click", function () {
            removeSplit("override")
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("save-button")
          .addEventListener("click", async function (e) {
            e.preventDefault()
            let buttonText = e.target.value
            e.target.value = "Please wait..."
            e.target.disabled = true
            let openingHours = openingHoursToSave(weekdays)
            if (openingHours) {
              try {
                let token = await utillogin.getClerkToken()
                let spaceId = parseInt(
                  document.getElementById("select-space").value
                )
                let openingHoursSplits = openingHours.map(function (d) {
                  let result = {
                    // capitalize first letter of day
                    day: d.day.charAt(0).toUpperCase() + d.day.slice(1),
                    is_open: d.isOpen,
                  }
                  if (d.isOpen) {
                    result.splits = d.splits.map(function (s) {
                      return {
                        start_time_local: s.startTime,
                        end_time_local: s.endTime,
                      }
                    })
                  }
                  return result
                })
                let saveStandardHours = await util.api
                  .post("save-standard-hours", {
                    json: {
                      token: token,
                      space_id: spaceId,
                      opening_hours: openingHoursSplits,
                    },
                  })
                  .json()
                if (saveStandardHours && saveStandardHours.success) {
                  util.logAmplitudeEvent("Opening Hours Saved")
                  Toastify({
                    text: "Your opening hours have been saved.",
                    duration: 4000,
                    close: true,
                    gravity: "top",
                    position: "center",
                    stopOnFocus: true,
                    style: {
                      background: "#1aa979",
                    },
                  }).showToast()
                } else {
                  util.logAmplitudeEvent("Opening Hours Save Failed")
                  document.getElementById("save-fail-modal").style.display =
                    "flex"
                  document.getElementById("modal-container").style.display =
                    "flex"
                }
              } catch (error) {
                util.logToSentry(error)
                util.logAmplitudeEvent("Unknown Error Saving Opening Hours")
                document.getElementById("save-fail-modal").style.display =
                  "flex"
                document.getElementById("modal-container").style.display =
                  "flex"
              }
            }
            e.target.disabled = false
            e.target.value = buttonText
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("save-override")
          .addEventListener("click", async function (e) {
            e.preventDefault()
            let buttonText = e.target.value
            e.target.value = "Please wait..."
            e.target.disabled = true
            let openingHours = openingHoursToSave(["override"])
            if (openingHours) {
              await saveOverrideHours(
                openingHours[0].splits.map(function (s) {
                  return {
                    start_time_local: s.startTime,
                    end_time_local: s.endTime,
                  }
                }),
                false
              )
            }
            e.target.disabled = false
            e.target.value = buttonText
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("delete-override")
          .addEventListener("click", async function (e) {
            e.preventDefault()
            if (e.target.value === "Remove override") {
              e.target.value = "Are you sure?"
              util.logAmplitudeEvent("Remove Override Hours Initial Click")
            } else {
              let buttonText = e.target.value
              e.target.value = "Please wait..."
              e.target.disabled = true
              await saveOverrideHours([], true) // delete by saving an empty array
              e.target.disabled = false
              e.target.value = buttonText
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("save-fail-modal-button")
          .addEventListener("click", function () {
            util.logAmplitudeEvent("Opening Hours Save Fail Refresh Clicked")
            window.location.reload()
          })
      } catch (err) {
        util.logToSentry(err)
      }
    }

    return {
      init: init,
    }
  })()
}
